import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Icon, type IconName } from "./Icon";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "small" | "medium";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly leadingIcon?: IconName;
  readonly trailingIcon?: IconName;
  readonly loading?: boolean;
  readonly children: ReactNode;
}

/** Consistent action control with shared loading, icon and focus behavior. */
export function Button({
  variant = "secondary",
  size = "medium",
  leadingIcon,
  trailingIcon,
  loading = false,
  className = "",
  disabled,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      aria-busy={loading || undefined}
      className={`button button-${variant} button-${size} ${className}`.trim()}
      disabled={disabled === true || loading}
      {...props}
    >
      {loading ? <span className="button-spinner" aria-hidden="true" /> : leadingIcon === undefined ? null : <Icon name={leadingIcon} size={16} />}
      <span>{children}</span>
      {trailingIcon === undefined ? null : <Icon name={trailingIcon} size={16} />}
    </button>
  );
}

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly label: string;
  readonly icon: IconName;
  readonly size?: ButtonSize;
}

/** Icon-only button that always has an accessible name. */
export function IconButton({ label, icon, size = "medium", className = "", ...props }: IconButtonProps) {
  return (
    <button
      aria-label={label}
      className={`icon-button icon-button-${size} ${className}`.trim()}
      title={label}
      type="button"
      {...props}
    >
      <Icon name={icon} size={size === "small" ? 16 : 18} />
    </button>
  );
}
